import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEventStore } from "@/stores/EventsStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { abortRequestsGetThatStartsWith, isBackendRequestPendingThatStartWith } from "@/utils/backend"
import { useStorage } from '@vueuse/core'
import debounce from 'lodash/debounce'
import { useUserStore } from "@/stores/UserStore"

export const useCustomerStore = defineStore('customerStore', () => {
  const { t } = useI18n()
  const eventStore = useEventStore()
  // const calendarStore = useCalendarStore()
  const userStore = useUserStore()

  const selectedCustomer = ref(null) // Holds currently selected customer object
  const searchedCustomer = ref('') // Holds the search term


  const updateSearchedCustomer = (searchTerm) => {
    if (searchTerm === "") return
    searchedCustomer.value = searchTerm
  }

  const blankCustomer = {
    salonerClientId: "",
    userId: "",
    name: "",
    surname: "",
    email: "",
    phone: "",
    additionalPhone: "",
    notes: "",
    groups: [],
    nip: "",
    pesel: null,
    birth: null,
    sex: "0",
    street: "",
    city: "",
    postalCode: "",
    notificationSms: "0",
    notificationEmail: "0",
    marketingSms: "0",
    marketingEmail: "0",
    blacklisted: "0",
  }

  const blankStatistics = {
    allVisits: 0,
    canceled: 0,
    factor: 0, // Współczynnik nieodbytych wizyt
    finished: 0,
    noShow: 0, // Wizyta nieodwołana
  }


  const gatheredUserInfo = ref(null)

  // Statistics - 1 voucher(bon) / 2 ticket(karnet)
  const customerVouchers = ref([]) // For tab 1 - vouchers
  const customerTickets = ref([])
  const customerHistoryVisits = ref([])
  const customerVouchersAndTickets = ref([])
  const customerStatistics = ref({ ...blankStatistics })
  const customerLoyaltyPoints = ref(0) // Loyalty points for the customer
  const isCustomerBlacklisted = ref(false) // Flag to indicate if the customer is blacklisted
  const incomingClientVisits = ref([]) // potential incoming visits for the client
  const suggestedServicesForClient = ref([]) // Suggested services based on client history, filled on client selection
  // const clientGroups = ref(calendarStore.clientGroups)

  const clearCustomerStatistics = () => {
    customerStatistics.value = { ...blankStatistics }
  }

  const setSelectedCustomer = async (customer) => {
    selectedCustomer.value = customer
  }

  const clearSearchedCustomer = () => searchedCustomer.value = ''

  const clearSelectedCustomer = () => {
    selectedCustomer.value = null
    customerVouchers.value = []
    customerTickets.value = []
    customerVouchersAndTickets.value = []
    customerHistoryVisits.value = []
    gatheredUserInfo.value = null
    attachmentCount.value = 0
    voucherCount.value = 0
    ticketCount.value = 0
    customerLoyaltyPoints.value = 0
    isCustomerBlacklisted.value = false
    eventStore.selectedLoyaltyCoupon = null
    incomingClientVisits.value = []
    suggestedServicesForClient.value = []
    if (!eventStore.isPrepaymentSectionDisabled) {
      eventStore.selectedPrepaymentMethod.email = ''
      eventStore.selectedPrepaymentMethod.phone = ''
    }
  }

  // Data fetched from the API
  const fetchedCustomers = ref([])

  // Local changes
  const newCustomers = ref([]) // Newly added customers
  const editedCustomers = ref([]) // Locally edited customers
  const deletedCustomers = ref([]) // Locally deleted customers
  const customersArray = ref([]) // Final combined array
  const attachmentCount = ref(0)

  // Loading flag
  const isLoading = ref(false)

  // Update the customersArray
  const updateCustomersArray = (searchTerm) => {
    //return fetchedCustomers.value
    let result = []
    try {
      if (fetchedCustomers.value === null) fetchedCustomers.value = []
      if (newCustomers.value === null) newCustomers.value = []
      result = [...fetchedCustomers.value, ...newCustomers.value]
    } catch (e) {
    }

    // Replace customers with their edited versions
    result = result.map(customer => {
      const edited = editedCustomers.value.find(c => c.id === customer.id)

      return edited ? { ...customer, ...edited } : customer
    })

    // Exclude customers in deletedCustomers
    result = result.filter(customer => !deletedCustomers.value.includes(customer.id))

    // Add pseudo-customer if no results are fetched and search term is valid
    // console.log('isLoading.value', isLoading.value)
    // console.warn('searchTerm', searchTerm, 'fetchedCustomers.value.length', fetchedCustomers.value.length)
    // console.warn('isBackendRequestPendingThatStartWith(/calendar/clients)', isBackendRequestPendingThatStartWith('/calendar/clients'))
    if (!isLoading.value && searchTerm && searchTerm.length > 2 && fetchedCustomers.value?.length === 0 && !isBackendRequestPendingThatStartWith('/calendar/clients')) {
      //console.error('Nie znaleziono! Dodaj nowego klienta')
      result = [
        {
          salonerClientId: "",
          userId: "",
          name: t('customer_not_found'),
          surname: "",
          email: "",
          phone: "",
          notes: "",
          groups: [],
          nip: "",
          pesel: "",
          birth: "",
          sex: "0",
          street: "",
          city: "",
          postalCode: "",
          notificationSms: "0",
          notificationEmail: "0",
          marketingSms: "0",
          marketingEmail: "0",
          blacklisted: "0",
        },
      ]
    }
    if (isLoading.value && searchTerm && searchTerm.length > 2) {
      result = [
        {
          disabled: true,
          salonerClientId: "",
          userId: "",
          name: t('search_in_progress') + '...',
          surname: '(' + t('click_to_cancel') + ')',
        },
      ]
    }

    customersArray.value = result
  }

  // Fetch customers from the API based on the search term
  const fetchCustomers = debounce(async (searchTerm = '') => {
    abortRequestsGetThatStartsWith('/calendar/clients')

    isLoading.value = true // Trick to cheat ppl perception ;-)
    updateCustomersArray(searchTerm) // Update customersArray even if the search term is invalid
    isLoading.value = false

    if (!searchTerm || searchTerm.length <= 2) {
      fetchedCustomers.value = []

      return
    }
    searchedCustomer.value = searchTerm // May be need in modal where you add new customer
    isLoading.value = true
    try {
      //const response = await fetch(`/api/apps/customers?search=${encodeURIComponent(searchTerm)}`)
      const queryParams = new URLSearchParams()
      queryParams.append('term', searchTerm)

      const response = await $backend(`/calendar/clients?${queryParams.toString()}`, {
        method: 'GET',
      })

      fetchedCustomers.value = response.data
    } catch (error) {
      // B: its needed here
    } finally {
      await nextTick()
      if (!isBackendRequestPendingThatStartWith('/calendar/clients')) isLoading.value = false
      updateCustomersArray(searchTerm) // Always update customersArray after fetching
    }
    fetchedCustomers.value = []
  }, 150)

  // Add a new customer locally
  const addCustomer = async (newCustomer) => {

    isLoading.value = true

    try {
      const response = await $backend(`/clients/addClient`, {
        method: 'POST',
        body: newCustomer,
      })

      if (response.success) {
        updateCustomersArray(newCustomer)
      }

      return response
    } catch (e) {
    } finally {
      isLoading.value = false
    }

    return {}
  }

  // Edit a customer locally
  const editCustomer = (updatedCustomer) => {
    const existingIndex = editedCustomers.value.findIndex(c => c.id === updatedCustomer.id)

    if (existingIndex !== -1) {
      // If the customer has already been edited, overwrite the data
      editedCustomers.value[existingIndex] = updatedCustomer
    } else {
      // Add the customer to the edited list if not already present
      editedCustomers.value.push(updatedCustomer)
    }

    updateCustomersArray()
  }

  // Delete a customer locally
  const deleteCustomer = (customerId) => {
    // Check if the customer is new and remove it from newCustomers if found
    const newIndex = newCustomers.value.findIndex(c => c.id === customerId)
    if (newIndex !== -1) {
      newCustomers.value.splice(newIndex, 1)
      updateCustomersArray()

      return
    }

    // Remove the customer from editedCustomers if present
    const editedIndex = editedCustomers.value.findIndex(c => c.id === customerId)
    if (editedIndex !== -1) {
      editedCustomers.value.splice(editedIndex, 1)
    }

    // Add the customer to deletedCustomers if not already present
    if (!deletedCustomers.value.includes(customerId)) {
      deletedCustomers.value.push(customerId)
    }

    updateCustomersArray()
  }

  // ========================================
  // Customers TAB
  // ========================================
  // Use useStorage for page and itemsPerPage
  const clientTablePage = useStorage('customer-tab-page', 1, localStorage, {
    listenToStorageChanges: false,
  })
  const clientTableItemsPerPage = useStorage('customer-tab-itemsPerPage', 10, localStorage, {
    listenToStorageChanges: false,
  })

  const DEFAULT_SORT = {
    key: 'id',
    order: 'desc',
  }
  const clientTabData = ref({
    isLoaded: false,
    customers: [],
    totalCustomers: 0,
    totalCustomersAbsolute: 0,
    page: clientTablePage.value,  // Initialize with storage value
    itemsPerPage: clientTableItemsPerPage.value,  // Initialize with storage value
    // Initialize as empty ref - will be replaced with salonerId-specific storage key via watch below
    // This prevents cache collision between different salon accounts by ensuring unique storage keys
    search: ref(''),
    // Initialize as empty ref - will be replaced with salonerId-specific storage key via watch below
    // This prevents cache collision between different salon accounts by ensuring unique storage keys
    selectedGroups: ref([]),
    sortBy: (() => {
      const stored = localStorage.getItem('customer-selected-sorting-option')
      if (stored) {
        const [key, order] = stored.split('-')

        return [{ key, order }]
      }

      return [{ ...DEFAULT_SORT }] // default
    })(),
    // Initialize as empty ref - will be replaced with salonerId-specific storage key via watch below
    // This prevents cache collision between different salon accounts by ensuring unique storage keys
    blacklistFilter: ref(null),
  })
  const resetSorting = () => {
    clientTabData.value.sortBy = [{ ...DEFAULT_SORT }]

    return clientTabData.value.sortBy
  }


  // Watch for current user ID and initialize storage with correct keys
  // This runs once currentUser?.id is available, ensuring each salon has its own isolated filter/search state
  watch(
    () => userStore.currentUser?.id,
    (id) => {
      if (id) {
        // search input cache
        clientTabData.value.search = useStorage(
          `clients-tab-search-${id}`,
          '',
          localStorage,
          {
            listenToStorageChanges: false,
          },
        )
        // selected groups cache
        clientTabData.value.selectedGroups = useStorage(
          `clients-tab-selectedGroups-${id}`,
          [],
          localStorage,
          {
            listenToStorageChanges: false,
          },
        )

        // Custom serializer for blacklist filter to handle boolean <-> string conversion in localStorage
        // Fixes VSelect displaying 'true'/'false' strings instead of translated labels after page refresh
        clientTabData.value.blacklistFilter = useStorage(
          `clients-tab-blacklistFilter-${id}`,
          null,
          localStorage,
          {
            listenToStorageChanges: false,
            serializer: {
              read: (v) => v === 'true' ? true : v === 'false' ? false : null,
              write: (v) => v === null ? null : String(v),
            },
          },
        )
      }
    },
    { immediate: true },
  )
  // Load customers (for customers TAB) with pagination, search, filters, sorting
  const loadCustomers = async (options) => {
    isLoading.value = true

    try {
      const queryParams = new URLSearchParams()

      // Sync pagination state across three sources:
      // 1. options - from VDataTableServer event
      // 2. clientTablePage/clientTableItemsPerPage - persistent storage (localStorage)
      // 3. clientTabData - reactive state for comparison
      if (options?.page) {
        clientTablePage.value = options.page          // Update localStorage
        clientTabData.value.page = options.page             // Update reactive state
      }
      if (options?.itemsPerPage) {
        clientTableItemsPerPage.value = options.itemsPerPage  // Update localStorage
        clientTabData.value.itemsPerPage = options.itemsPerPage     // Update reactive state
      }

      // Store sortBy for future comparison to detect sorting changes
      if (options?.sortBy) {
        clientTabData.value.sortBy = options.sortBy
      }

      // Add pagination parameters to API request
      queryParams.append('page', clientTabData.value.page)
      queryParams.append('perPage', clientTabData.value.itemsPerPage)

      if (clientTabData.value.search) {
        queryParams.append('filters[search]', clientTabData.value.search)
      }

      // Add group filters - each selected group is added as separate array element
      // Backend expects: filters[groups][]=599&filters[groups][]=527
      if (clientTabData.value.selectedGroups && clientTabData.value.selectedGroups.length > 0) {
        clientTabData.value.selectedGroups.forEach(group => {
          queryParams.append('filters[groups][]', group.id)
        })
      }

      // Add blacklist filter if checkbox is checked
      // Backend expects: filters[blacklisted]=true or false as string
      if (clientTabData.value.blacklistFilter !== null) {
        queryParams.append('filters[blacklisted]', clientTabData.value.blacklistFilter ? 'true' : 'false')
      }
      // Handle sorting parameters
      // VDataTableServer provides sortBy array with {key, order} objects
      // Backend expects: orders[0][column]=surname&orders[0][direction]=asc
      if (options?.sortBy?.length) {
        options.sortBy.forEach((sort, index) => {
          queryParams.append(`orders[${index}][column]`, sort.key)
          queryParams.append(`orders[${index}][direction]`, sort.order)
        })
      } else {
        // Default sorting by creation date descending when no sorting specified
        // queryParams.append('orders[0][column]', 'id')
        // queryParams.append('orders[0][direction]', 'desc')

        queryParams.append('orders[0][column]', clientTabData.value.sortBy[0].key)
        queryParams.append('orders[0][direction]', clientTabData.value.sortBy[0].order)
      }

      // Fetch customers from API with all query parameters
      const response = await $backend(`/clients/index?${queryParams.toString()}`, {
        method: 'GET',
      })

      if (response.success) {
        // If we are on a far away page and there are no results for it we go to the last page instead.
        // This happens e.g. when switching salons while a high page number is remembered in localStorage.
        if (response.data.clients.length === 0 && clientTabData.value.page !== 1 && response.meta?.total > 0) {
          clientTabData.value.isLoaded = false
          const lastPage = Math.max(1, Math.ceil(response.meta.total / clientTabData.value.itemsPerPage))
          // CRITICAL: sync items-length BEFORE changing the page. VDataTableServer derives
          // pageCount from :items-length, and an internal watcher clamps page back down when
          // page > pageCount. While totalCustomers is still 0/stale, pageCount is 1, so setting
          // the page to e.g. 2 would be immediately clamped to 1 (see vuetify paginate.js).
          clientTabData.value.totalCustomers = response.meta.total
          clientTabData.value.totalCustomersAbsolute = response.meta.totalAll
          clientTablePage.value = lastPage

          return true
        }
        // Mark data as loaded to enable caching
        clientTabData.value.isLoaded = true
        // Update customer list and total count for pagination
        clientTabData.value.customers = response.data.clients
        clientTabData.value.totalCustomers = response.meta.total
        clientTabData.value.totalCustomersAbsolute = response.meta.totalAll
      }
    } catch (error) {
      // Silently handle errors (could add toast notification here)
    } finally {
      isLoading.value = false
    }
  }

  // ========================================
  // Client Groups | Customer Groups Management Modal State
  // ========================================
  const groupsModalPage = useStorage('groups-modal-page', 1, localStorage, {
    listenToStorageChanges: false,
  }) // pagination
  const groupsModalItemsPerPage = useStorage('groups-modal-itemsPerPage', 5, localStorage, {
    listenToStorageChanges: false,
  }) // pagination
  const groupsModalTotalItems = ref(0) // pagination
  const isLoadingGroupsModal = ref(false)
  const paginatedClientGroups = ref([]) // used inside modal vdatatables
  const allClientGroups = ref([]) // used in autocomplete input in main page
  const groupsSelectedSortOption = ref('id-desc') // default sort option
  const hasClientGroupsChanged = ref(false) // flag to indicate if groups have changed and need reload

  // Sorting options for client groups in autocomplete input
  const sortOptions = [
    { title: t('newest'), value: 'id-desc' },
    { title: t('oldest'), value: 'id-asc' },
    { title: t('name_a_z'), value: 'name-asc' },
    { title: t('name_z_a'), value: 'name-desc' },
  ]

  // Independent loading of 'paginated' client groups (used in client tab groups modal)
  const loadClientGroups = async (options = {}) => {
    try {
      const page = options.page ?? groupsModalPage.value
      const perPage = options.itemsPerPage ?? groupsModalItemsPerPage.value

      if (options.page !== undefined) groupsModalPage.value = options.page
      if (options.itemsPerPage !== undefined) groupsModalItemsPerPage.value = options.itemsPerPage

      const queryParams = new URLSearchParams()
      queryParams.append('page', page)
      queryParams.append('perPage', perPage)

      // Add sorting parameters
      if (options.sortBy?.length) {
        options.sortBy.forEach((sort, index) => {
          queryParams.append(`orders[${index}][column]`, sort.key)
          queryParams.append(`orders[${index}][direction]`, sort.order)
        })
      }

      const response = await $backend(`/clientGroups/index?${queryParams.toString()}`, {
        method: 'GET',
      })

      if (response?.data) {
        paginatedClientGroups.value = response.data.groups
      }

      return {
        groups: response?.data?.groups || [],
        total: response?.meta?.total || 0,
      }
    } catch (error) {
      return { groups: [], total: 0 }
    }
  }

  // Load all client groups for autocomplete input
  const loadAllClientGroups = async (force = false, options = {}) => {
    if (!force && allClientGroups.value.length > 0) {
      return
    }

    try {
      const queryParams = new URLSearchParams()
      queryParams.append('page', 1)
      queryParams.append('perPage', -1) // Fetch all groups

      // Add sorting parameters - default to name asc
      const sortBy = options.sortBy?.length ? options.sortBy : [{ key: 'name', order: 'asc' }]
      sortBy.forEach((sort, index) => {
        queryParams.append(`orders[${index}][column]`, sort.key)
        queryParams.append(`orders[${index}][direction]`, sort.order)
      })

      const response = await $backend(`/clientGroups/index?${queryParams.toString()}`, {
        method: 'GET',
      })

      if (response?.data) {
        allClientGroups.value = response.data.groups
      }


      return {
        groups: response?.data?.groups || [],
        total: response?.meta?.total || 0,
        success: response?.success || false,
      }
    } catch (error) {
      return { groups: [], total: 0 }
    } finally {
      hasClientGroupsChanged.value = false
    }
  }

  // ========================================
  // 👉 Merge Customers
  // ========================================
  const mergeCustomers = async (selectedCustomersIDs, mergeIntoId) => {
    isLoading.value = true

    try {
      return await $backend('/clients/merge', {
        method: 'POST',
        body: {
          salonerClients: selectedCustomersIDs,
          mergeIntoSalonerClient: mergeIntoId,
        },
      })
    } catch (e) {
      return { success: false }
    } finally {
      isLoading.value = false
    }
  }

  // ========================================
  // 👉 Customer Card
  // 👉 Get information
  // ========================================
  const voucherCount = ref(0)
  const ticketCount = ref(0)

  const getCustomerInformation = async (salonerClientId, type = null) => {
    isLoading.value = true

    try {
      const response = await $backend(`/clients/getClientInfo`, {
        method: 'GET',
        params: { id: salonerClientId },
      })

      if (response.success) {
        await setSelectedCustomer(response.data.salonerClient)
        gatheredUserInfo.value = response.data
        attachmentCount.value = response.data?.attachments?.length || 0
        voucherCount.value = response.data?.vouchers?.giftCardsCount
        ticketCount.value = response.data?.vouchers?.passesCount
        customerStatistics.value = response.data?.stats
        // showSuccessToast(response.message)
      }

      return response
    } catch (e) {
    } finally {
      isLoading.value = false
    }

    return {}
  }

  // 👉 Update information
  const updateCustomerInformation = async (customerData) => {
    if (!customerData || !customerData.salonerClientId) {
      console.error('No customer data or ID provided')

      return
    }
    isLoading.value = true

    try {

      const response = await $backend(`/clients/updateClient`, {
        method: 'POST',
        body: customerData,
        headers: {
          'Content-Type': 'application/json',
        },

      })

      if (response.success) {
        // Update the selected customer with the returned data
        selectedCustomer.value = response.data.salonerClient

        // Update the customer in editedCustomers if present
        const index = editedCustomers.value.findIndex(c =>
          c.id === payload.id || c.salonerClientId === payload.id,
        )

        if (index !== -1) {
          editedCustomers.value[index] = { ...editedCustomers.value[index], ...response.data }
        }

        // Update the customers array to reflect the changes
        updateCustomersArray(selectedCustomer.value)
        // await setSelectedCustomer(selectedCustomer.value)

        showSuccessToast(t('savedChanges'))
      }

      return response
    } catch (e) {
    } finally {
      isLoading.value = false
    }

    return {}
  }

  // 👉 VOUCHERS & TICKETS /CUSTOMER CARD
  const ticketsTableOptions = ref({
    page: 1,
    perPage: useStorage('customer-tickets-per-page', 10, localStorage, {
      listenToStorageChanges: false,
    }),
    total: 0,
    search: '',
    // 'all' | 'isActive' | 'isUsed' | 'isExpired' — 'all' = no filter in the request
    filters: {
      status: 'isActive',
    },
    sortBy: useStorage('tickets-selected-sorting-option', 'isActive:desc', localStorage, {
      listenToStorageChanges: false,
    }),
  })

  // Vouchers — same mechanism as ticketsTableOptions
  const vouchersTableOptions = ref({
    page: 1,
    perPage: useStorage('customer-vouchers-per-page', 10, localStorage, {
      listenToStorageChanges: false,
    }),
    total: 0,
    search: '',
    // 'all' | 'isActive' | 'isUsed' | 'isExpired' — 'all' sends no parameter to the BE
    filters: {
      status: 'isActive',
    },
    sortBy: useStorage('vouchers-selected-sorting-option', 'isActive:desc', localStorage, {
      listenToStorageChanges: false,
    }),
  })

  // 1 Bon (Voucher)
  // 2 Ticket (karnet)
  // Get specific vouchers or tickets
  const getCustomerVouchers = async (customerId, type = null, options = {}) => {

    try {
      abortRequestsGetThatStartsWith('/clients/getVouchers')
    } catch (error) {
    }

    isLoading.value = true

    try {
      const {
        orderColumn = 'isActive',
        orderDirection = 'desc',
        search = '',
        perPage = 10,
        page = 1,
        filters = null,
      } = options

      const queryParams = new URLSearchParams()
      queryParams.append('id', customerId)

      if (type !== null) {
        queryParams.append('type', type)
      }

      // Add pagination and sorting parameters
      queryParams.append('orders[0][column]', orderColumn)
      queryParams.append('orders[0][direction]', orderDirection)
      queryParams.append('perPage', perPage)
      queryParams.append('page', page)

      if (search) {
        queryParams.append('search', search)
      }

      // filters[isActive] | filters[isUsed] | filters[isExpired] — the backend accepts the value 1
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            queryParams.append(`filters[${key}]`, value)
          }
        })
      }

      const response = await $backend(`/clients/getVouchers?${queryParams.toString()}`, {
        method: 'GET',
      })

      // Handle the new response format
      if (type === 1) {
        // Vouchers
        customerVouchers.value = response.data?.vouchers || []

        // Add vouchers to selectedCustomer if it exists
        if (selectedCustomer.value) {
          selectedCustomer.value.vouchers = customerVouchers.value
        }
      } else if (type === 2) {
        // Tickets
        customerTickets.value = response.data?.vouchers || []

        // Add tickets to selectedCustomer if it exists
        if (selectedCustomer.value) {
          selectedCustomer.value.tickets = customerTickets.value
        }
      }

      // Return the full response for pagination handling
      return response
    } catch (e) {
    } finally {
      if (!isBackendRequestPendingThatStartWith('/clients/getVouchers')) isLoading.value = false
    }

    return { data: { vouchers: [] }, meta: { total: 0, page: 1, perPage: 5 }, success: false }
  }

  // Get customer vouchers and tickets combined
  const getCustomerVouchersAndTickets = async (customerId, options = {}) => {
    isLoading.value = true

    try {
      const {
        orderColumn = '',
        orderDirection = '',
        search = '',
      } = options

      const queryParams = new URLSearchParams()
      queryParams.append('id', customerId)

      // Add pagination and sorting parameters
      queryParams.append('orderColumn', orderColumn)
      queryParams.append('orderDirection', orderDirection)

      if (search) {
        queryParams.append('search', search)
      }

      const response = await $backend(`/clients/getStatsAndBenefits?${queryParams.toString()}`, {
        method: 'GET',
      })


      if (response.success) {
        customerVouchers.value = response.data.vouchers || []
        customerTickets.value = response.data.passes || []
        customerVouchersAndTickets.value = [...response.data.vouchers] || []
        customerStatistics.value = { ...response.data.stats } || blankStatistics
        customerLoyaltyPoints.value = Number(response.data.loyaltyProgram.loyaltyPoints)
      }

      // Return the full response data for pagination
      return response.data
    } catch (e) {
    } finally {
      isLoading.value = false
    }

    return { rows: [] }
  }

  ///clients/getVoucherUsageHistory?id=8197
  const historyVouchersOrTickets = ref([])
  // Get voucher or ticket usage history
  const getVoucherUsageHistory = async (id) => {
    try {

      const response = await $backend(`/clients/getVoucherUsageHistory`, {
        method: 'GET',
        params: { id: id },
      })

      if (response.success) {
        historyVouchersOrTickets.value = response.data
      } else {

        return false
      }

      return response.data || []
    } catch (error) {
    }
  }


  // Update loyalty points
  const updateCustomerLoyaltyPoints = async (salonerClientId, points) => {
    isLoading.value = true
    try {

      const response = await $backend(`/clients/updateLoyaltyProgramPoints`, {
        method: 'POST',
        body: {
          id: salonerClientId,
          points: points,
        },
      })

      if (response.success) {
        customerLoyaltyPoints.value = Number(response.data.loyaltyProgram.loyaltyPoints) || 0

        return true
      }

    } catch (e) {
    } finally {
      isLoading.value = false
    }

    return false
  }


  // Handle black list
  const toggleCustomerBlacklist = async (salonerClientId, action) => {
    isLoading.value = true
    try {
      return await $backend(`/clients/updateClientsBlacklist`, {
        method: 'POST',
        body: {
          clients: salonerClientId,
          type: action, //add, remove
        },
      })
    } catch (error) {
    } finally {
      isLoading.value = false
    }
  }

  // Delete customer
  const deleteCustomerPermanently = async (salonerClientId) => {
    isLoading.value = true
    try {
      return await $backend(`/clients/deleteClients`, {
        method: 'POST',
        body: {
          clients: salonerClientId,
        },
      })
    } catch (error) {
    } finally {
      isLoading.value = false
    }
  }

  //Visits history
  const getVisitsHistory = async (salonerClientId, options = {}) => {
    isLoading.value = true

    // orderColumn
    // Available options:
    // "start_date",
    // "status",
    // "description",
    // "service",
    // "employee",
    // "product",
    // "duration",
    // "loyaltyProgram",
    // "price"

    // orderDirection
    // "asc",
    // "desc"

    try {
      const {
        orderColumn = 'start_date',
        orderDirection = 'desc',
        search = '',
        page = 1,
        perPage = 10,
      } = options

      const queryParams = new URLSearchParams()
      queryParams.append('id', salonerClientId)
      queryParams.append('orders[0][column]', orderColumn)
      queryParams.append('orders[0][direction]', orderDirection)
      queryParams.append('page', page)
      queryParams.append('perPage', perPage)

      if (search) {
        queryParams.append('search', search)
      }

      const response = await $backend(`/clients/getVisits?${queryParams.toString()}`, {
        method: 'GET',
      })

      customerHistoryVisits.value = response.data?.visits || []

      // Return the full response including meta for pagination
      return {
        visits: response.data?.visits || [],
        meta: response.meta,
      }
    } catch (e) {
    } finally {
      isLoading.value = false
    }

    return { visits: [], meta: { total: 0, page: 1, perPage: 5 } }
  }

  // ============================================================================
  // Visits history — VisitCard shape (visits + product sales)
  // ============================================================================

  /**
   * Filters and sorting for the "Historia wizyt" tab.
   *
   * Persisted under their own localStorage keys so they never collide with the
   * Statistics -> Visits list state, which uses the same filter names.
   */
  const visitsHistoryOptions = ref({
    page: 1,
    perPage: useStorage('customer-history-items-per-page', 10, localStorage, {
      listenToStorageChanges: false,
    }),
    total: 0,
    search: '',
    filters: {
      status: useStorage('customerHistory-filter-status', null, localStorage, {
        listenToStorageChanges: false,
      }),
      visitSource: useStorage('customerHistory-filter-visitSource', null, localStorage, {
        listenToStorageChanges: false,
      }),
      employeeId: useStorage('customerHistory-filter-employeeId', null, localStorage, {
        listenToStorageChanges: false,
      }),
    },
    sortBy: useStorage('customerHistory-selected-sorting-option-v2', 'date-desc', localStorage, {
      listenToStorageChanges: false,
    }),
  })

  const clearVisitsHistoryFilters = () => {
    visitsHistoryOptions.value.filters.status = null
    visitsHistoryOptions.value.filters.visitSource = null
    visitsHistoryOptions.value.filters.employeeId = null
    visitsHistoryOptions.value.search = ''
    visitsHistoryOptions.value.page = 1
  }

  /**
   * Builds the query for /clients/getVisitsAndSales and /clients/exportVisits.
   * Both endpoints accept the very same parameters, which is what keeps the
   * spreadsheet in sync with what is on screen.
   *
   * @param {number|string} salonerClientId
   * @returns {URLSearchParams}
   */
  const buildVisitsHistoryParams = (salonerClientId) => {
    const opt = visitsHistoryOptions.value
    const params = new URLSearchParams()

    params.append('id', salonerClientId)
    params.append('page', opt.page)
    params.append('perPage', opt.perPage)

    if (opt.search) params.append('search', opt.search)

    if (opt.filters.status) params.append('filters[status]', opt.filters.status)
    if (opt.filters.visitSource) params.append('filters[visitSource]', opt.filters.visitSource)
    if (opt.filters.employeeId) params.append('filters[employeeId]', opt.filters.employeeId)

    const [column, direction] = String(opt.sortBy).split('-')
    params.append('orders[0][column]', column)
    params.append('orders[0][direction]', direction)

    return params
  }

  /**
   * Fetches the client visit history in the VisitCard shape.
   * Returns the raw visits so the caller can map them with mapStatsVisitToCard().
   */
  const getVisitsAndSales = async (salonerClientId) => {
    if (!salonerClientId) return { visits: [], meta: null }

    try {
      abortRequestsGetThatStartsWith('/clients/getVisitsAndSales')
    } catch (error) {
    }

    isLoading.value = true

    try {
      const params = buildVisitsHistoryParams(salonerClientId)

      const response = await $backend(`/clients/getVisitsAndSales?${params.toString()}`, {
        method: 'GET',
      })

      customerHistoryVisits.value = response.data?.visits || []

      if (response.meta) {
        visitsHistoryOptions.value.total = response.meta.total
        visitsHistoryOptions.value.page = response.meta.page
      }

      return { visits: customerHistoryVisits.value, meta: response.meta }
    } catch (e) {
      return { visits: [], meta: null }
    } finally {
      if (!isBackendRequestPendingThatStartWith('/clients/getVisitsAndSales')) isLoading.value = false
    }
  }

  // Cancel visit
  const cancelVisit = async (eventId, includeCyclic = 1) => {
    isLoading.value = true
    try {
      const response = await $backend(`/events/cancel`, {
        method: 'POST',
        body: {
          eventId: eventId,
          includeCyclicEvent: includeCyclic, // 0 - no, 1 - yes
        },
      })

      return response.success
    } catch (e) {
    } finally {
      isLoading.value = false
    }

    return false
  }


  // const attachmentCount = ref(gatheredUserInfo.value.attachments.length)
  // 👉 Files management

  // 👉 Uploading
  const uploadFile = async (salonerClientId, file, hash) => {
    // isLoading.value = true
    try {
      const formData = new FormData()
      formData.append('salonerClientId', salonerClientId)
      formData.append('type', 'clientCard')
      formData.append('files[]', file)

      const response = await $backend(`/files/upload?hash=${hash}`, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        method: 'POST',
        body: formData,
      })

      if (response.success) {
        // Refresh customer data to update attachments
        //B: TODO: jakos inaczej to zapisac
        await getCustomerInformation(salonerClientId)
      }

      return response.success
    } catch (error) {
    }

    return false
  }

  // 👉 Deletation
  const deleteFile = async (salonerClientId, id) => {
    try {

      const response = await $backend(`/files/delete`, {
        method: 'POST',
        body: { id: Number(id) },
      })


      if (response.success) {
        //Refresh customer data
        //B: TODO: jakos inaczej to zapisac
        await getCustomerInformation(salonerClientId)
      }

      return response.success
    } catch (error) {
    }

    return false
  }


  // 👉 Download
  const downloadFile = async (fileId) => {
    try {
      // Get the file with proper response handling
      const blob = await $backend(`/files/download`, {
        method: 'GET',
        params: { id: fileId },
        responseType: 'blob',
      })

      if (!(blob instanceof Blob)) {
        console.error('Invalid response: expected Blob')

        return false
      }
      // Default filename if we can't get a better one
      // let filename = `file-${fileId}`
      //
      // // Try to get the original filename from gatheredUserInfo
      // if (gatheredUserInfo.value?.attachments) {
      //   const fileInfo = gatheredUserInfo.value.attachments.find(f => f.id === fileId)
      //   if (fileInfo?.originalName) {
      //     filename = fileInfo.originalName
      //   }
      // }
      //
      // // Create and trigger download if we have a blob
      // const blobUrl = URL.createObjectURL(blob)
      // const a = document.createElement('a')
      // a.href = blobUrl
      // a.download = filename
      // document.body.appendChild(a)
      // a.click()
      //
      // await nextTick()
      //
      // document.body.removeChild(a)
      // URL.revokeObjectURL(blobUrl)


      return blob

    } catch (error) {

      return false
    }
  }

  const generateFilePreviewLink = async (fileId) => {
    isLoading.value = true
    try {

      const queryParams = new URLSearchParams()
      queryParams.append('id', fileId)

      const response = await $backend(`/files/signedUrl?${queryParams.toString()}`, {
        method: 'GET',
      })


      if (response) {
        return response.data.signedUrl
      } else {

        return false
      }
    } catch (error) {
      return false
    } finally {
      isLoading.value = false
    }
  }

  //Messages
  // filter (query params): 0 - all, 1 - unread messages, 2 - read messages
  // lastStatus: 1 = last time client wrote to You
  // lastStatus: 0 = last time You wrote to the client
  // salonerStatus: 1 = there are unread messages
  // salonerStatus: 0 = all messages read

  const sendMessage = async (recipientId, message, allowDiacritics) => {
    try {
      const response = await $backend(`/messages/sendSms`, {
        method: 'POST',
        body: {
          recipientId: recipientId,
          message: message,
          allowDiacritics: allowDiacritics,
          // topicId: topicId, // m: tego chyba nie trzeba?, jest w postmanie
        },
      })

      if (response.success) {
        return response
      }
    } catch (e) {
      console.error('Error sending message', e)
    }

    return false
  }

  //Visit history change logs
  const getVisitChangeLogs = async (eventId) => {
    isLoading.value = true
    try {

      const queryParams = new URLSearchParams()
      queryParams.append('event', eventId)

      const response = await $backend(`/events/history?${queryParams.toString()}`, {
        method: 'GET',
      })
      isLoading.value = false

      return response
    } catch (error) {
    } finally {
      isLoading.value = false
    }

    return {}
  }

  // Check if client exists in system (by phone number)
  const checkIfClientExists = async (phone) => {
    try {

      const queryParams = new URLSearchParams()
      queryParams.append('phone', phone)

      const response = await $backend(`/clients/checkClientExists?${queryParams.toString()}`, {
        method: 'GET',
      })

      if (response.success) {
        return response
      }
    } catch (error) {
    }

    return {}
  }

  // Delete store selling from visits history
  const deleteProductSale = async (id) => {
    try {
      const response = await $backend(`/productSales/delete`, {
        method: 'POST',
        body: { id: id },
      })

      if (response.success) {
        showSuccessToast(response.message)
      }

      return response
    } catch (error) {
    }

    return {}
  }


  // Generic function to toggle state but only in STORE:
  // // - customer blacklist status (client_blacklist)
  // // - customer marketing consents (client_sms_marketing, client_email_marketing)
  const toggleCustomerFlagInStore = (flag, salonerClientId, state) => {
    const targetCustomer = clientTabData.value.customers.find(c => c.salonerClientId === salonerClientId)

    if (targetCustomer) {
      const flagMap = {
        'client_blacklist': 'blacklisted',
        'client_sms_marketing': 'marketingSms',
        'client_email_marketing': 'marketingEmail',
        'client_sms_visit_reminder': 'notificationSms',
        'client_email_visit_reminder': 'notificationEmail',
      }

      const propertyName = flagMap[flag]
      if (propertyName) {
        targetCustomer[propertyName] = state
      }
    }
  }

  // Generic function to toggle:
  const toggleCustomerFlag = async (flag, salonerClientId, state) => {
    try {
      toggleCustomerFlagInStore(flag, salonerClientId, state)
      const response = await $backend(`/clients/setFlag?t=${Date.now()}`, {
        method: 'POST',
        body: {
          flag: flag,
          salonerClientId: salonerClientId,
          state: state,
        },
      })

      if (response.success) {
        showSuccessToast(response.message)
      } else {
        toggleCustomerFlagInStore(flag, salonerClientId, !state)
      }

      return response
    } catch (error) {
      toggleCustomerFlagInStore(flag, salonerClientId, !state)
    }

    return {}
  }

  return {
    // --- Core Customer Data ---
    searchedCustomer,           // Current search term
    selectedCustomer,           // Currently selected customer
    customersArray,             // Combined list of customers
    fetchedCustomers,           // Raw customers from API
    newCustomers,               // Locally added customers
    editedCustomers,            // Locally edited customers
    deletedCustomers,           // Locally deleted customers
    blankCustomer,              // Template for new customer
    incomingClientVisits,       // Potential incoming visits for the client
    suggestedServicesForClient, // Suggested services based on client history
    // clientGroups,               // Client groups from calendar store
    clientTabData,
    clientTablePage, // Pagination state for customers TAB
    clientTableItemsPerPage, // Pagination state for customers TAB
    groupsModalPage,
    groupsModalItemsPerPage,
    groupsModalTotalItems,
    isLoadingGroupsModal,
    paginatedClientGroups,
    allClientGroups,
    groupsSelectedSortOption,
    sortOptions,
    resetSorting,
    hasClientGroupsChanged,

    // --- Customer Details ---
    gatheredUserInfo,           // Detailed customer information
    attachmentCount,            // Number of customer attachments

    // --- Customer Statistics ---
    customerVouchers,           // Customer's vouchers/gift cards
    customerTickets,            // Customer's passes/tickets
    customerHistoryVisits,      // Customer's visit history
    customerVouchersAndTickets, // Combined vouchers and tickets
    voucherCount,               // Number of vouchers
    ticketCount,                // Number of tickets
    historyVouchersOrTickets,   // Voucher or ticket usage history
    customerStatistics,         // Events counter like finished, canceled, etc.
    customerLoyaltyPoints,
    isCustomerBlacklisted,

    // --- UI State ---
    isLoading,                  // Loading state indicator

    // --- Customer Selection Methods ---
    setSelectedCustomer,        // Set the current customer
    updateSearchedCustomer,     // Update search term
    clearSearchedCustomer,      // Clear search term
    clearSelectedCustomer,      // Clear selected customer

    // --- Data Operations ---
    fetchCustomers,             // Search and fetch customers
    updateCustomersArray,       // Update the combined customers list
    addCustomer,                // Add new customer
    editCustomer,               // Edit existing customer
    deleteCustomer,             // Delete customer locally
    checkIfClientExists,
    loadCustomers,              // Load customers for customers TAB with pagination, search, filters, sorting
    loadClientGroups,          // Load client groups independently
    loadAllClientGroups,       // Load all client groups independently
    mergeCustomers,

    // --- Customer Information Management ---
    getCustomerInformation,     // Get detailed customer info
    updateCustomerInformation,  // Update customer information
    updateCustomerLoyaltyPoints, // Update loyalty points
    toggleCustomerBlacklist,    // Add/remove from blacklist
    deleteCustomerPermanently,  // Permanently delete customer
    toggleCustomerFlag,

    // --- Vouchers and Tickets ---
    getCustomerVouchers,        // Get vouchers or tickets
    getCustomerVouchersAndTickets, // Get both vouchers and tickets
    ticketsTableOptions,        // Paging / search / sorting for the passes list
    vouchersTableOptions,
    getVoucherUsageHistory,
    deleteProductSale, // Delete store selling from visits history
    clearCustomerStatistics,    // Clear customer statistics

    // --- Visit Management ---
    getVisitsHistory,           // Get customer visit history (legacy table shape)
    getVisitsAndSales,          // Get customer visit history + product sales (VisitCard shape)
    visitsHistoryOptions,       // Filters / sorting / paging for the visits history tab
    clearVisitsHistoryFilters,
    buildVisitsHistoryParams,   // Shared by the list request and the XLS export
    cancelVisit,                // Cancel a scheduled visit
    //customerInSalon,            // Toggle customer presence in salon
    getVisitChangeLogs,         // Get visit change logs

    // --- File Management ---
    uploadFile,                 // Upload customer file
    downloadFile,               // Download customer file
    deleteFile,                 // Delete customer file
    generateFilePreviewLink,

    // --- Communication ---
    sendMessage,
  }
})
